import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  SortingState,
  TableState,
  useReactTable,
} from '@tanstack/react-table';
import { Table as ReactTable } from '@tanstack/table-core/build/lib/types';
import * as React from 'react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import eventService from '@service/eventService';

import { dateFilter } from './filterHandlers/dateFilter';
import { fuzzyFilter } from './filterHandlers/fuzzy';
import { minMaxFilter } from './filterHandlers/minMaxFilter';
import { multiDropDownFilter } from './filterHandlers/multiDropDownFilter';
import { penceMinMaxFilter } from './filterHandlers/penceMinMaxFilter';
import { DEFAULT_TABLE_PAGE_SIZE } from './tableHelpers';
import { TableServices } from './TableService';

type FilterStateParam = ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState);

declare module '@tanstack/table-core' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
    datetime: FilterFn<unknown>;
    minMax: FilterFn<unknown>;
    penceMinMax: FilterFn<unknown>;
    multiDropDownFilter: FilterFn<unknown>;
  }
}

export type FilterState = ColumnFiltersState | Map<string, unknown>;

export type TablePaginationRef = {
  paginationStates: (string | undefined)[];
  currentPageIndex: number;
};

export type TableRefType = {
  getPaginationState: () => TablePaginationRef | null;
};

type TableContextProps<TData> = {
  columns: ColumnDef<TData, unknown>[];
  getRowId: (row: Row<TData>) => string;
  getTable: () => ReactTable<TData> | null;
  _rawColumnFilterState: ColumnFiltersState;
  columnFilterState: Map<string, unknown>;
  sortingState: SortingState;
  selected: Set<string>;
  filter: (columnId: string, value: unknown) => Promise<void>;
  select: (row: Row<TData>) => void;
  selectAll: (value: boolean) => void;
  resetFilters: (newState?: FilterState) => Promise<void>;
  resetSelected: () => void;
  filteredData: TData[];
  _rawData: TData[];
  name: string;
  isRowSelectable: (rowId: string) => boolean;
  isSortable: boolean | 'custom';
  isSelectable?: boolean;
  isFilterable?: boolean | 'custom';
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const TableContext = createContext<TableContextProps>({
  columns: [],
  getRowId: () => '',
  getTable: () => null,
  _rawColumnFilterState: [],
  columnFilterState: new Map(),
  sortingState: [],
  selected: new Set(),
  filter: async () => {},
  select: () => {},
  selectAll: () => {},
  resetFilters: async () => {},
  resetSelected: () => {},
  filteredData: [],
  _rawData: [],
  name: '',
  isRowSelectable: () => false,
  isSortable: false,
  isSelectable: false,
  isFilterable: false,
});

export type TableOnFilterChangeFn<TData> = (
  newState: ColumnFiltersState,
  isReset?: boolean,
) => void | Promise<{ data?: TData[]; filters?: ColumnFiltersState } | void>;

export type TableProviderProps<TData> = {
  data: TData[] | null;
  columns: ColumnDef<TData, unknown>[];
  isPaginated?: boolean | 'custom';
  paginationState?: PaginationState;
  isSortable?: boolean | 'custom';
  isSelectable?: boolean;
  selectAllOnePageAtATime?: boolean;
  onSortChange?: (newState: SortingState) => void;
  onSelectionChange?: (selection: Set<string>) => void;
  selectionIdProperty?: string;
  nonSelectableIds?: string[];
  isFilterable?: boolean | 'custom';
  onFilterChange?: TableOnFilterChangeFn<TData>;
  initialColumnFilterState?: ColumnFiltersState;
  initialSortingState?: SortingState;
  forceClientSideFilterColumnIds?: string[];
  debugTable?: boolean;
  debugHeaders?: boolean;
  debugColumns?: boolean;
  name?: string;
};

function TableProvider<TData>({
  data,
  columns,
  isSortable = false,
  isPaginated,
  paginationState,
  isSelectable,
  selectAllOnePageAtATime,
  onSortChange,
  onSelectionChange,
  selectionIdProperty,
  nonSelectableIds,
  isFilterable,
  onFilterChange,
  initialColumnFilterState,
  initialSortingState,
  forceClientSideFilterColumnIds,
  debugTable,
  debugHeaders,
  debugColumns,
  name,
  children,
}: TableProviderProps<TData> & { children: ReactNode[] | ReactNode }) {
  const tableName = useMemo(() => name ?? `table-${uuidv4()}`, [name]);

  const [sortingState, setSortingState] = useState<SortingState>(initialSortingState || []);
  const [columnFilterState, setColumnFilterState] = useState<ColumnFiltersState>(
    initialColumnFilterState ?? [],
  );
  const [globalFilter, setGlobalFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [localData, setLocalData] = useState<TData[]>(data ?? []);
  const [unfilteredData, setUnfilteredData] = useState<TData[]>(data ?? []);

  const tableState: Partial<TableState> = {
    sorting: sortingState,
    columnFilters: columnFilterState,
    globalFilter,
  };
  if (paginationState) tableState.pagination = paginationState;
  const hasInitialSortState = Array.isArray(initialSortingState) && initialSortingState.length > 0;

  const columnFilterStateMap = useMemo(() => {
    const newMap = new Map<string, unknown>();
    columnFilterState.forEach(({ id, value }) => newMap.set(id, value));
    return newMap;
  }, [columnFilterState]);

  const handleSortChange = (newState: SortingState | ((old: SortingState) => SortingState)) => {
    const parsedNewState = typeof newState == 'function' ? newState(sortingState) : newState;
    setSortingState(newState);
    onSortChange?.(parsedNewState);
  };

  const filterLocalData = (newData: TData[], filterState: ColumnFiltersState) => {
    if (filterState.length && Array.isArray(forceClientSideFilterColumnIds)) {
      if (Array.isArray(newData)) {
        const filterFnMap = new Map<string, FilterFn<TData>>();

        forceClientSideFilterColumnIds.forEach((columnId) => {
          const column = tableRef.getColumn(columnId);
          const filterFn = column?.getFilterFn();
          if (filterFn) filterFnMap.set(columnId, filterFn);
        });

        /*
          Tanstack wasn't designed to have a combination of client and server side filters
          the @ts-ignore's are unavoidable to properly implement this feature without heavy code
          duplication
         */
        newData = newData.filter((row: TData) => {
          const getValue = (columnId: string): unknown => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return row?.[columnId] || '';
          };

          for (const columnFilter of filterState) {
            const columnId = columnFilter.id;

            if (filterFnMap.has(columnId)) {
              const filterFn = filterFnMap.get(columnId);
              if (filterFn) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const ret = filterFn({ getValue }, columnId, columnFilter.value, () => {});
                if (!ret) return false;
              }
            }
          }
          return true;
        });
      }
    }
    return newData;
  };

  const handleFilterChange = async (newState: FilterStateParam, isReset = false) => {
    const willTriggerUpdate = (
      stateMap1: Map<string, unknown>,
      stateMap2: Map<string, unknown>,
    ) => {
      if (
        !Array.isArray(forceClientSideFilterColumnIds) ||
        forceClientSideFilterColumnIds.length === 0
      ) {
        return true;
      }

      for (const [id, value] of stateMap1.entries()) {
        if (!forceClientSideFilterColumnIds.includes(id)) {
          const newValue = stateMap2.get(id);
          return JSON.stringify(newValue) !== JSON.stringify(value);
        }
      }
    };

    const parsedNewState = typeof newState == 'function' ? newState(columnFilterState) : newState;
    const oldState = [...columnFilterState];

    //TODO: Find better solution for race conditions and re-renders
    const onFilterChangeResponse = await onFilterChange?.(parsedNewState, isReset);
    if (!!onFilterChangeResponse && Array.isArray(onFilterChangeResponse?.filters)) {
      setColumnFilterState(onFilterChangeResponse?.filters as ColumnFiltersState);
    } else {
      setColumnFilterState(parsedNewState);
    }

    const oldStateMap = new Map(oldState.map((filter) => [filter.id, filter.value]));
    const newStateMap = new Map(parsedNewState.map((filter) => [filter.id, filter.value]));

    if (Array.isArray(forceClientSideFilterColumnIds)) {
      if (
        !willTriggerUpdate(oldStateMap, newStateMap) &&
        !willTriggerUpdate(newStateMap, oldStateMap)
      ) {
        /* When mixing server and client side filtering,
      we only want to filter the data locally here if we don't already trigger a server side change
       */
        let newData = onFilterChangeResponse?.data;
        if (newData === undefined) newData = [...unfilteredData];
        setLocalData(filterLocalData(newData, parsedNewState));
      }
    }
  };

  const resetFilters = async (newState?: FilterState) => {
    let filterStateToUse: ColumnFiltersState = [];
    if (!!newState) {
      if (Array.isArray(newState)) filterStateToUse = newState;
      else {
        newState.forEach((value, key) => {
          filterStateToUse.push({ id: key, value });
        });
      }
    } else {
      filterStateToUse = initialColumnFilterState ?? [];
    }
    await handleFilterChange(filterStateToUse || [], true);
  };

  const setFilter = async (columnId: string, value: unknown) => {
    const newFilters = structuredClone(columnFilterState);
    const currentFilterIndex = newFilters.findIndex((filter) => filter.id === columnId);
    const existingFilterExists = currentFilterIndex > -1;

    if (value === null) {
      if (existingFilterExists) newFilters.splice(currentFilterIndex, 1);
    } else {
      if (existingFilterExists) {
        newFilters[currentFilterIndex].value = value;
      } else {
        newFilters.push({ id: columnId, value });
      }
    }
    tableRef.setColumnFilters(newFilters);
    return handleFilterChange(newFilters);
  };

  const tableRef: ReactTable<TData> = useReactTable({
    enableSorting: !!isSortable || hasInitialSortState,
    initialState: {
      columnFilters: initialColumnFilterState,
      pagination: {
        pageSize: DEFAULT_TABLE_PAGE_SIZE,
      },
    },
    manualSorting: isSortable === 'custom',
    data: localData,
    columns,
    state: tableState,
    filterFns: {
      fuzzy: fuzzyFilter,
      datetime: dateFilter,
      minMax: minMaxFilter,
      penceMinMax: penceMinMaxFilter,
      multiDropDownFilter: multiDropDownFilter,
    },
    //onColumnFiltersChange: handleFilterChange,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    onSortingChange: handleSortChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualFiltering: isFilterable === 'custom',
    getSortedRowModel: isSortable || hasInitialSortState ? getSortedRowModel() : undefined,
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getPaginationRowModel: isPaginated === true ? getPaginationRowModel() : undefined,
    debugTable,
    debugHeaders,
    debugColumns,
  });

  const getRowId = (row: Row<TData>) => {
    if (selectionIdProperty) {
      const rowData = localData?.[row.index];
      if (rowData) {
        const prop = selectionIdProperty as keyof TData;
        return `${rowData[prop]}`;
      }
    }
    return row.id;
  };

  const isRowSelectable = useCallback(
    (rowId: string) => !nonSelectableIds?.includes(rowId),
    [nonSelectableIds],
  );

  const handleSelectAll = (value: boolean) => {
    const newSet = new Set<string>(selectAllOnePageAtATime ? selected : null);
    const { rows } = selectAllOnePageAtATime ? tableRef.getRowModel() : tableRef.getCoreRowModel();

    if (value) {
      rows.forEach((row) => {
        const rowId = getRowId(row);
        if (isRowSelectable(rowId)) {
          newSet.add(rowId);
        }
      });
    } else if (selectAllOnePageAtATime) {
      rows.forEach((row) => newSet.delete(getRowId(row)));
    }
    setSelected(newSet);
    onSelectionChange?.(newSet);
  };

  const handleSelect = (row: Row<TData>) => {
    const rowId = getRowId(row);

    const newSet = new Set<string>(selected);
    if (newSet.has(rowId)) newSet.delete(rowId);
    else {
      newSet.add(rowId);
    }
    setSelected(newSet);
    onSelectionChange?.(newSet);
  };

  const handleResetSelected = async () => {
    const newSet = new Set<string>();
    setSelected(newSet);
    onSelectionChange?.(newSet);
  };

  const handleGetTable = () => tableRef;

  useEffect(() => {
    setUnfilteredData(data ?? []);
    setLocalData(filterLocalData(data ?? [], columnFilterState)); // Falling back to an empty array so we clear localData whenever data becomes null
  }, [data]);

  useEffect(() => {
    eventService.on(TableServices.SetTableFilter, setFilter);
    eventService.on(TableServices.ResetTableFilters, resetFilters);
    eventService.on(TableServices.ResetTableSelected, handleResetSelected);
    return () => {
      eventService.removeListener(TableServices.SetTableFilter);
      eventService.removeListener(TableServices.ResetTableFilters);
      eventService.removeListener(TableServices.ResetTableSelected);
    };
  }, [resetFilters, setFilter]);

  return (
    <TableContext.Provider
      value={{
        getRowId,
        getTable: handleGetTable,
        columns,
        _rawColumnFilterState: columnFilterState,
        columnFilterState: columnFilterStateMap,
        sortingState,
        selected,
        filter: setFilter,
        select: handleSelect,
        selectAll: handleSelectAll,
        resetFilters: resetFilters,
        resetSelected: handleResetSelected,
        _rawData: unfilteredData,
        filteredData: localData,
        name: tableName,
        isRowSelectable,
        isFilterable,
        isSelectable,
        isSortable,
      }}>
      {children}
    </TableContext.Provider>
  );
}

function useTableContext<TData>() {
  return useContext<TableContextProps<TData>>(TableContext);
}

export { useTableContext, TableProvider };
